import { getSurgeryPresetFilters, isActivationPreset, SurgeryPreset } from "features/dashboard/utils/tileLinks";
import { DEFAULT_SORT, FIELD_BY_KEY, SORTABLE_KEYS } from "./patientTableFields";

/**
 * PIPCARE-1358: the Patients table's whole view lives in the URL, so it can be bookmarked,
 * shared, and saved to the sidebar. Example:
 *   /patients?search=smith&filter=hospital:eq:DHEC&filter=activation_status:eq:pending&sort=-surgery_date,last_name&page=2
 */
export interface PatientTableFilter {
  field: string;
  operator: string;
  /** Practitioner filters hold several ids. */
  value: string | string[];
}

export interface PatientTableState {
  search: string;
  filters: PatientTableFilter[];
  sort: string[];
  page: number;
}

const LIST_OPERATORS = ["in", "not_in"];
const LIST_SEPARATOR = "|";

const isSortable = (key: string): boolean => (SORTABLE_KEYS as readonly string[]).includes(key);

const parseFilter = (raw: string): PatientTableFilter | null => {
  const first = raw.indexOf(":");
  const second = raw.indexOf(":", first + 1);
  if (first <= 0 || second <= first) return null;

  const field = raw.slice(0, first);
  const operator = raw.slice(first + 1, second);
  const value = raw.slice(second + 1);
  if (!FIELD_BY_KEY[field] || value === "") return null;

  return {
    field,
    operator,
    value: LIST_OPERATORS.includes(operator) ? value.split(LIST_SEPARATOR).filter(Boolean) : value,
  };
};

const fromLegacyPreset = (preset: string): Pick<PatientTableState, "filters" | "sort"> | null => {
  let settings;
  try {
    settings = getSurgeryPresetFilters(preset as SurgeryPreset);
  } catch {
    return null;
  }
  if (!settings?.filters) return null;

  const filters = settings.filters
    .map((json: string) => JSON.parse(json) as PatientTableFilter)
    .map(({ field, operator, value }) => ({ field, operator, value: String(value) }));
  return { filters, sort: isActivationPreset(preset) ? ["created_at"] : DEFAULT_SORT };
};

export const parsePatientTableQuery = (query: string): PatientTableState => {
  const params = new URLSearchParams(query);

  // Dashboard tiles and old bookmarks still use ?preset=…; turn them into real filters.
  const preset = params.get("preset");
  const legacy = preset ? fromLegacyPreset(preset) : null;

  const filters = legacy?.filters ?? params.getAll("filter").map(parseFilter).filter(Boolean);
  const sortParam = params.get("sort") ?? "";
  const requestedSort = sortParam.split(",").map((s) => s.trim()).filter((s) => isSortable(s.replace(/^-/, "")));
  // The API rejects a blank sort_by.
  const sort = legacy?.sort ?? (requestedSort.length ? requestedSort : DEFAULT_SORT);
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);

  return { search: params.get("search") ?? "", filters: filters as PatientTableFilter[], sort, page };
};

export const serializePatientTableQuery = (state: PatientTableState): string => {
  const params = new URLSearchParams();
  if (state.search.trim()) params.set("search", state.search.trim());
  state.filters.forEach(({ field, operator, value }) => {
    params.append("filter", `${field}:${operator}:${Array.isArray(value) ? value.join(LIST_SEPARATOR) : value}`);
  });
  if (state.sort.length && state.sort.join(",") !== DEFAULT_SORT.join(",")) params.set("sort", state.sort.join(","));
  if (state.page > 1) params.set("page", String(state.page));

  // Keep `:`, `,` and `|` readable in bookmarks.
  const query = params.toString().replace(/%3A/g, ":").replace(/%2C/g, ",").replace(/%7C/g, "|");
  return query ? `?${query}` : "";
};

/** The `filters[]` strings `api.client.Patient.list` and the CSV export expect. */
export const toApiFilters = (filters: PatientTableFilter[]): string[] =>
  filters.map(({ field, operator, value }) => JSON.stringify({ field, operator, value }));
